/**
 * Typed model over the get_festival_map() RPC payload.
 *
 * The RPC returns one GeoJSON FeatureCollection (or null — no published map).
 * This module is the only place that payload's shape is interpreted: parsing,
 * per-kind display metadata, and the small helpers the map view and the
 * nearest-amenity readout share. Pure and framework-free, tested headlessly
 * like trackingRadarGeometry.ts.
 */

import { CHIP_KINDS as VOCAB_CHIP_KINDS, KINDS, KIND_ORDER, isFeatureKind } from './kinds';
import type { FeatureKind } from './kinds';

export type { FeatureKind } from './kinds';

export type MapFeature = {
  id: string;
  kind: FeatureKind;
  name: string | null;
  lineupStageName: string | null;
  geometry: { type: 'Point' | 'Polygon'; coordinates: unknown };
  /** Representative point — the Point itself, or the polygon's centroid. */
  anchorLat: number;
  anchorLng: number;
};

export type FestivalMapModel = {
  editionId: string;
  eventId: string;
  center: { lat: number; lng: number } | null;
  bounds: { west: number; south: number; east: number; north: number } | null;
  features: MapFeature[];
  /** OSM-derived ground snapshot (migration 119); © OpenStreetMap
   * contributors. Passed straight to the terrain layers; null = plain ground. */
  terrain: object | null;
};

/**
 * Display metadata per kind, derived from mapKinds.json (label, colour, whether it renders
 * as a 26px glyph tile). The colours are the 3D discs' colours — since 2026-09-17 the
 * chip, the tile and the disc are one palette. The four staleness bucket colours
 * (#22c55e #94a3b8 #f59e0b #ef4444) are reserved for peer trust and never reused for
 * kinds — medical's red is the one deliberate exception, matching every first-aid sign.
 */
export const KIND_DISPLAY: Record<
  FeatureKind,
  { label: string; color: string; service?: boolean }
> = Object.fromEntries(
  KIND_ORDER.map((k) => [
    k,
    { label: KINDS[k].label, color: KINDS[k].color, service: KINDS[k].service },
  ]),
) as Record<FeatureKind, { label: string; color: string; service?: boolean }>;

/** Anchors: always drawn, never filterable (handoff §4 — gates included). */
export const ANCHOR_KINDS: readonly FeatureKind[] = ['stage', 'entrance', 'exit'];

/** Zone kinds: a Polygon renders as a tinted wash; a Point as a pin. */
export const ZONE_KINDS: readonly FeatureKind[] = [
  'vip',
  'ada',
  'ada_checkin',
  'camping',
  'ga_area',
];

/** The index tail — stored and searchable, never offered as a chip. */
export const INDEX_KINDS: readonly FeatureKind[] = KIND_ORDER.filter(
  (k) => KINDS[k].family === 'index' && KINDS[k].chip === null,
);

/**
 * Chip order in the row and the settings sheet — by how often and how urgently
 * a chip is reached for, not by corpus frequency.
 *
 * The row is ONE stable order, so it cannot follow the arc of a festival day
 * (arrive → be there → leave). Ordered by expected total reaches instead, in
 * four tiers:
 *
 *   body      what you need repeatedly for hours, plus medical, which is rare
 *             but must never be scrolled for. Handoff §5's "four needs first".
 *   arrival   getting in and out. Rare per person but near-universal, and it
 *             is what a map is open for before the gates and after the encore.
 *   access    the things you look up once and act on — ADA, info, lockers.
 *   incidental everything you reach for only if you happen to want it.
 *
 * Only kinds the edition actually has become chips, so most venues show a
 * handful of these; the order is what decides which end they land at.
 *
 * The order itself lives in mapKinds.json (vocabulary order = row order); this is that
 * list with the anchors that double as chips (entrance, exit) where the phases can
 * promote them.
 */
export const CHIP_KINDS: readonly FeatureKind[] = VOCAB_CHIP_KINDS;

/**
 * Where the user is in the arc of the day. The chip row leads with different
 * things in each.
 *
 * Standing outside the fence nobody is looking for a restroom — they want the
 * gate, the box office, the check-in desk. Inside, it inverts: the body wins
 * and the way out is irrelevant until it isn't. Position answers in/out and
 * the lineup answers early/late, so both are already to hand.
 */
export type ChipPhase = 'arriving' | 'inside' | 'leaving';

/** The last stretch of the event, when the way out starts to matter to
 * someone who is still inside. */
export const LEAVING_TAIL_MS = 60 * 60 * 1000;

/**
 * Kinds that jump to the front of the row in a given phase. Everything else
 * keeps CHIP_KINDS order behind them.
 *
 * A lead list rather than three full orderings: three 23-entry arrays would
 * have to be kept in step every time a kind is added, and the base order IS
 * the inside order, so `inside` needs no lead at all.
 */
export const PHASE_LEAD: Record<ChipPhase, readonly FeatureKind[]> = {
  // Outside, heading in: how do I get in, and what has to happen first.
  arriving: [
    'entrance',
    'box_office',
    'ada_checkin',
    'id_check',
    'info',
    'lockers',
    'rideshare',
    'shuttle',
    'parking',
    'bike',
  ],
  // The base order already leads with the body.
  inside: [],
  // The gate you leave by and the ride home — whichever ride it is. Lockers
  // because there is something in one.
  leaving: ['exit', 'rideshare', 'shuttle', 'parking', 'bike', 'lockers'],
};

/**
 * Which phase the user is in.
 *
 * `insideVenue` null means no position — on the web, or location off. Then the
 * only signal is the clock, and before the first set "arriving" is the better
 * prior than showing someone on their sofa where the water is.
 *
 * With no set times ingested (`eventEndMs` null) this degrades to position
 * alone, which is still most of the value: in means inside, out means arriving.
 */
export function chipPhase(input: {
  insideVenue: boolean | null;
  nowMs: number;
  eventStartMs: number | null;
  eventEndMs: number | null;
}): ChipPhase {
  const { insideVenue, nowMs, eventStartMs, eventEndMs } = input;
  const ended = eventEndMs != null && nowMs >= eventEndMs;
  const tailing = eventEndMs != null && nowMs >= eventEndMs - LEAVING_TAIL_MS;

  if (insideVenue === true) return tailing ? 'leaving' : 'inside';
  // Outside with the event still running is someone arriving — or arriving
  // late. Either way they want the gate, not the bar. Only once it is over
  // does outside mean gone.
  if (insideVenue === false) return ended ? 'leaving' : 'arriving';
  if (eventStartMs != null && nowMs < eventStartMs) return 'arriving';
  return ended ? 'leaving' : 'inside';
}

/**
 * The chips an edition offers, in CHIP_KINDS order.
 *
 * A merged chip takes the slot of its FIRST present member rather than being
 * appended. It used to be pushed on after the filter, which pinned Rides to
 * the end of every row no matter where `rideshare` sat in the order — the
 * order was not expressible, not merely wrong.
 */
export function chipRowKinds(
  presentKinds: ReadonlySet<string>,
  groups: Readonly<Record<string, readonly string[]>>,
  phase: ChipPhase = 'inside',
): FeatureKind[] {
  const headFor = new Map<string, string>();
  for (const [head, members] of Object.entries(groups)) {
    for (const member of members) headFor.set(member, head);
  }
  const row: FeatureKind[] = [];
  const seen = new Set<string>();
  const offer = (kind: FeatureKind) => {
    if (!presentKinds.has(kind)) return;
    const head = (headFor.get(kind) ?? kind) as FeatureKind;
    if (seen.has(head)) return;
    seen.add(head);
    row.push(head);
  };
  // The phase's lead first, in ITS order, then everything else in base order.
  for (const kind of PHASE_LEAD[phase]) offer(kind);
  for (const kind of CHIP_KINDS) offer(kind);
  return row;
}

type Position = [number, number];

function polygonCentroid(ring: Position[]): Position {
  // Vertex mean is enough here: stage footprints are small convex-ish quads,
  // and the centroid only anchors a marker/label.
  let lng = 0;
  let lat = 0;
  // A closed ring repeats its first vertex; skip the duplicate.
  const last =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.length - 1
      : ring.length;
  for (let i = 0; i < last; i += 1) {
    lng += ring[i][0];
    lat += ring[i][1];
  }
  return [lng / last, lat / last];
}

/** Parse the RPC payload. Returns null for null/malformed input — the caller
 * treats both as "no map", never as an error the user should see. */
export function parseFestivalMap(payload: unknown): FestivalMapModel | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;
  if (raw.type !== 'FeatureCollection') return null;
  const props = (raw.properties ?? {}) as Record<string, unknown>;
  if (typeof props.edition_id !== 'string' || typeof props.event_id !== 'string') return null;

  const features: MapFeature[] = [];
  for (const f of Array.isArray(raw.features) ? raw.features : []) {
    const feat = f as Record<string, unknown>;
    const geometry = feat.geometry as MapFeature['geometry'] | undefined;
    const fp = (feat.properties ?? {}) as Record<string, unknown>;
    if (!geometry || (geometry.type !== 'Point' && geometry.type !== 'Polygon')) continue;

    let anchor: Position;
    if (geometry.type === 'Point') {
      anchor = geometry.coordinates as Position;
    } else {
      const rings = geometry.coordinates as Position[][];
      if (!rings?.[0]?.length) continue;
      anchor = polygonCentroid(rings[0]);
    }
    if (!Number.isFinite(anchor?.[0]) || !Number.isFinite(anchor?.[1])) continue;

    const kind: FeatureKind = isFeatureKind(fp.kind) ? fp.kind : 'other';
    features.push({
      id: String(feat.id ?? `${kind}-${features.length}`),
      kind,
      name: typeof fp.name === 'string' ? fp.name : null,
      lineupStageName: typeof fp.lineup_stage_name === 'string' ? fp.lineup_stage_name : null,
      geometry,
      anchorLng: anchor[0],
      anchorLat: anchor[1],
    });
  }

  const center = props.center as FestivalMapModel['center'];
  const bounds = props.bounds as FestivalMapModel['bounds'];
  const terrain = props.terrain as { type?: string } | null | undefined;
  return {
    editionId: props.edition_id,
    eventId: props.event_id,
    center: center && Number.isFinite(center.lat) && Number.isFinite(center.lng) ? center : null,
    bounds: bounds && Number.isFinite(bounds.west) ? bounds : null,
    features,
    terrain: terrain && terrain.type === 'FeatureCollection' ? terrain : null,
  };
}

/** Polygons of the given kinds as a FeatureCollection for a MapLibre layer. */
export function polygonCollection(
  model: FestivalMapModel,
  kinds: readonly FeatureKind[],
): { type: 'FeatureCollection'; features: object[] } {
  const wanted = new Set(kinds);
  return {
    type: 'FeatureCollection',
    features: model.features
      .filter((f) => f.geometry.type === 'Polygon' && wanted.has(f.kind))
      .map((f) => ({
        type: 'Feature',
        id: f.id,
        geometry: f.geometry,
        properties: { kind: f.kind, name: f.name },
      })),
  };
}

/** Back-compat alias: the original stage-footprint collection. */
export function stagePolygonCollection(model: FestivalMapModel) {
  return polygonCollection(model, ['stage']);
}

/** Bounding box of one polygon feature's outer ring, for georeferenced
 * radial-wash image sources. Null for points. */
export function polygonBbox(
  feature: MapFeature,
): { west: number; south: number; east: number; north: number } | null {
  if (feature.geometry.type !== 'Polygon') return null;
  const ring = (feature.geometry.coordinates as [number, number][][])[0] ?? [];
  if (!ring.length) return null;
  const lngs = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  return {
    west: Math.min(...lngs),
    east: Math.max(...lngs),
    south: Math.min(...lats),
    north: Math.max(...lats),
  };
}

/**
 * Pair a co-located entrance + exit into one two-way gate (handoff §4: the
 * paired tiles in a wider fence gap). Two gate points within ~40 m merge.
 */
export function pairGates(model: FestivalMapModel): Array<{
  key: string;
  lat: number;
  lng: number;
  name: string | null;
  entrance: MapFeature | null;
  exit: MapFeature | null;
}> {
  const gates = model.features.filter(
    (f) => (f.kind === 'entrance' || f.kind === 'exit') && f.geometry.type === 'Point',
  );
  const used = new Set<string>();
  const out: Array<{
    key: string;
    lat: number;
    lng: number;
    name: string | null;
    entrance: MapFeature | null;
    exit: MapFeature | null;
  }> = [];
  for (const g of gates) {
    if (used.has(g.id)) continue;
    used.add(g.id);
    const mate = gates.find(
      (m) =>
        !used.has(m.id) &&
        m.kind !== g.kind &&
        Math.abs(m.anchorLat - g.anchorLat) < 0.00036 &&
        Math.abs(m.anchorLng - g.anchorLng) < 0.00043,
    );
    if (mate) used.add(mate.id);
    const entrance = g.kind === 'entrance' ? g : mate?.kind === 'entrance' ? mate : null;
    const exit = g.kind === 'exit' ? g : mate?.kind === 'exit' ? mate : null;
    out.push({
      key: g.id,
      lat: mate ? (g.anchorLat + mate.anchorLat) / 2 : g.anchorLat,
      lng: mate ? (g.anchorLng + mate.anchorLng) / 2 : g.anchorLng,
      name: entrance?.name ?? exit?.name ?? null,
      entrance,
      exit,
    });
  }
  return out;
}

/** Transport lives outside the fence and can be a kilometre away (Ocean Way's
 * lots are ~1 km down Barnard Way). It must never define the camera. */
export const TRANSPORT_KINDS: readonly FeatureKind[] = [
  'rideshare',
  'shuttle',
  'parking',
  'bike',
  'box_office',
];

function boundsOf(points: Array<[number, number]>): [[number, number], [number, number]] | null {
  if (!points.length) return null;
  const lngs = points.map((p) => p[0]);
  const lats = points.map((p) => p[1]);
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
}

function pointsOf(features: MapFeature[]): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const f of features) {
    if (f.geometry.type === 'Polygon') {
      for (const ring of f.geometry.coordinates as [number, number][][]) {
        out.push(...ring);
      }
    } else {
      out.push([f.anchorLng, f.anchorLat]);
    }
  }
  return out;
}

/**
 * The festival footprint — what the camera frames and what peer privacy is
 * measured against. Prefers the authored venue polygon (ga_area); falls back
 * to everything except transport. Distinct from cameraBounds(), which spans
 * every feature so the user can still pan out to the lots.
 */
export function venueBounds(model: FestivalMapModel): [[number, number], [number, number]] | null {
  const fence = model.features.filter((f) => f.kind === 'ga_area' && f.geometry.type === 'Polygon');
  if (fence.length) return boundsOf(pointsOf(fence));
  const onSite = model.features.filter((f) => !TRANSPORT_KINDS.includes(f.kind));
  return boundsOf(pointsOf(onSite)) ?? cameraBounds(model);
}

/**
 * Oriented (minimum-area-ish) rectangle for a polygon, as four corners in
 * MapLibre image-source order. A beach strip runs diagonally, so its
 * axis-aligned bbox is nearly square — painting a wash there covers four
 * times the ground it should. Rotating to the polygon's longest edge fixes it.
 */
export function orientedRect(feature: MapFeature): Array<[number, number]> | null {
  if (feature.geometry.type !== 'Polygon') return null;
  const ring = (feature.geometry.coordinates as [number, number][][])[0] ?? [];
  if (ring.length < 3) return null;

  const lat0 = ring.reduce((s, p) => s + p[1], 0) / ring.length;
  const lng0 = ring.reduce((s, p) => s + p[0], 0) / ring.length;
  const kx = Math.cos((lat0 * Math.PI) / 180);
  const toM = (p: [number, number]): [number, number] => [
    (p[0] - lng0) * 111320 * kx,
    (p[1] - lat0) * 111320,
  ];
  const toDeg = (p: [number, number]): [number, number] => [
    lng0 + p[0] / (111320 * kx),
    lat0 + p[1] / 111320,
  ];

  const m = ring.map(toM);
  let bestLen = -1;
  let angle = 0;
  for (let i = 0; i < m.length - 1; i += 1) {
    const dx = m[i + 1][0] - m[i][0];
    const dy = m[i + 1][1] - m[i][1];
    const len = Math.hypot(dx, dy);
    if (len > bestLen) {
      bestLen = len;
      angle = Math.atan2(dy, dx);
    }
  }
  const c = Math.cos(-angle);
  const sn = Math.sin(-angle);
  const rot = m.map(([x, y]): [number, number] => [x * c - y * sn, x * sn + y * c]);
  const xs = rot.map((p) => p[0]);
  const ys = rot.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const back = ([x, y]: [number, number]): [number, number] => [
    x * Math.cos(angle) - y * Math.sin(angle),
    x * Math.sin(angle) + y * Math.cos(angle),
  ];
  // MapLibre image coordinates: top-left, top-right, bottom-right, bottom-left.
  return [
    toDeg(back([minX, maxY])),
    toDeg(back([maxX, maxY])),
    toDeg(back([maxX, minY])),
    toDeg(back([minX, minY])),
  ];
}

/** The oriented rect's two side lengths in metres, longest first. */
export function orientedSpanMeters(feature: MapFeature): { longM: number; shortM: number } | null {
  const rect = orientedRect(feature);
  if (!rect) return null;
  const kx = Math.cos((rect[0][1] * Math.PI) / 180);
  const a = Math.hypot((rect[1][0] - rect[0][0]) * 111320 * kx, (rect[1][1] - rect[0][1]) * 111320);
  const b = Math.hypot((rect[2][0] - rect[1][0]) * 111320 * kx, (rect[2][1] - rect[1][1]) * 111320);
  return { longM: Math.max(a, b), shortM: Math.min(a, b) };
}

/**
 * The zone a chip is actually about — the largest polygon among its kinds.
 *
 * A chip's pins usually sit INSIDE something: bikes has Bike Parking in Lot 4
 * South, rides has the rideshare and shuttle points in Lot 5 South, tickets IS
 * the box office compound, ADA is the check-in. Treating the two as one thing
 * is what makes tapping a chip read as "take me to the box office" rather than
 * "move the camera vaguely over there and highlight nothing".
 *
 * Derived from the geometry rather than a per-venue table — every chip with an
 * enclosing zone has exactly one polygon among its kinds, and largest-first is
 * the honest tiebreak if a venue ever has two. `ga_area` is never a chip's
 * zone: it is the whole site.
 */
export function zoneForKinds(model: FestivalMapModel, kinds: readonly string[]): MapFeature | null {
  let best: MapFeature | null = null;
  let bestArea = 0;
  for (const feature of model.features) {
    if (!kinds.includes(feature.kind)) continue;
    if (feature.kind === 'ga_area') continue;
    if (feature.geometry.type !== 'Polygon') continue;
    const span = orientedSpanMeters(feature);
    if (!span) continue;
    const area = span.longM * span.shortM;
    if (area > bestArea) {
      best = feature;
      bestArea = area;
    }
  }
  if (best) return best;

  // No polygon of its own: the lot the chip's pins stand in. Rides, Shuttle and
  // Parking were one chip until 2026-09-17, and "the zone is the lot its pins
  // sit in" held only because the lot was a member. Now it has to be geometry:
  // the parking polygon containing the most of this chip's points wins.
  const pins = model.features.filter((f) => kinds.includes(f.kind) && f.geometry.type === 'Point');
  if (!pins.length) return null;
  let bestLot: MapFeature | null = null;
  let bestHits = 0;
  for (const lot of model.features) {
    if (lot.kind !== 'parking' || lot.geometry.type !== 'Polygon') continue;
    const ring = (lot.geometry.coordinates as Position[][])[0];
    if (!ring) continue;
    const hits = pins.filter((p) => pointInRing([p.anchorLng, p.anchorLat], ring)).length;
    if (hits > bestHits) {
      bestLot = lot;
      bestHits = hits;
    }
  }
  return bestLot;
}

/** Even-odd point-in-ring test on [lng, lat] pairs. */
export function pointInRing(point: Position, ring: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    const crosses =
      yi > point[1] !== yj > point[1] && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

/**
 * Zoom at which a zone's minor axis reaches `targetPx`.
 *
 * Zone names only draw once the on-screen minor axis clears the label ladder,
 * so "select the box office" has to carry a zoom or the name it selected stays
 * invisible: the box office is 44 x 46 m, which is ~30 px at the default fit —
 * under the ladder's 48. The minor axis is the binding one; a long thin lot
 * clears on its length long before you can read a name across its width.
 */
export function zoomForZoneLabel(feature: MapFeature, targetPx: number): number | null {
  const span = orientedSpanMeters(feature);
  if (!span || span.shortM <= 0) return null;
  return zoomForMeters(span.shortM, targetPx, feature.anchorLat);
}

/**
 * Map bearing that lays the venue's long axis across the screen.
 *
 * Festival sites are usually elongated and rarely north-aligned — Ocean Way is
 * a 599 x 194 m beach strip running NW-SE, whose north-up bounding box is
 * 531 x 547 m. Fitting that square letterboxes the site into a fraction of the
 * panel. Rotating to the site's own axis is also what every official festival
 * map does; north-up is not a virtue here.
 *
 * Returns 0 when the venue is roughly square (nothing to gain) or unauthored.
 */
export function venueBearing(model: FestivalMapModel): number {
  const fence = model.features.find((f) => f.kind === 'ga_area' && f.geometry.type === 'Polygon');
  if (!fence) return 0;
  const rect = orientedRect(fence);
  if (!rect) return 0;
  const kx = Math.cos((rect[0][1] * Math.PI) / 180);
  const east = (rect[1][0] - rect[0][0]) * 111320 * kx;
  const north = (rect[1][1] - rect[0][1]) * 111320;
  const side = (rect[2][0] - rect[1][0]) * 111320 * kx;
  const sideN = (rect[2][1] - rect[1][1]) * 111320;
  const longLen = Math.hypot(east, north);
  const shortLen = Math.hypot(side, sideN);
  // Square-ish venues gain nothing from rotation and lose north-up familiarity.
  if (longLen < shortLen * 1.35) return 0;
  // Compass bearing of the long axis, minus 90 so it lies across the screen.
  const axis = (Math.atan2(east, north) * 180) / Math.PI;
  const bearing = (((axis - 90) % 360) + 360) % 360;
  // Either direction lays it flat; pick the one closest to north-up.
  return bearing > 180 ? bearing - 180 : bearing;
}

/**
 * Everything the camera needs to frame the venue as it actually lies.
 *
 * fitBounds() cannot express this: it takes an axis-aligned box, so handing it
 * the venue bbox plus a bearing makes it reserve room for the ROTATED SQUARE
 * (531 x 547 m rotated 48 deg needs ~762 m), and the site renders at half the
 * width it should. Framing the oriented rectangle directly is exact — the
 * caller turns longM/shortM into a zoom against its own viewport.
 */
export function venueFrame(model: FestivalMapModel): {
  center: [number, number];
  longM: number;
  shortM: number;
  bearing: number;
} | null {
  const fence = model.features.find((f) => f.kind === 'ga_area' && f.geometry.type === 'Polygon');
  const rect = fence ? orientedRect(fence) : null;
  if (!rect) return null;
  const center: [number, number] = [(rect[0][0] + rect[2][0]) / 2, (rect[0][1] + rect[2][1]) / 2];
  const kx = Math.cos((center[1] * Math.PI) / 180);
  const dist = (a: [number, number], b: [number, number]) =>
    Math.hypot((a[0] - b[0]) * 111320 * kx, (a[1] - b[1]) * 111320);
  const side1 = dist(rect[0], rect[1]);
  const side2 = dist(rect[1], rect[2]);
  return {
    center,
    longM: Math.max(side1, side2),
    shortM: Math.min(side1, side2),
    bearing: venueBearing(model),
  };
}

/**
 * The default view once a festival's maps are stitched: every feature (venue
 * plus the parking/transport map's lots), measured and centred along the
 * VENUE's own axes so the rotation still follows the site.
 *
 * Span and centre must come from the same origin — measuring the extent about
 * the venue centre while centring the camera on the outer centre pushed the
 * westernmost stage off the panel.
 */
export function outerFrame(model: FestivalMapModel): {
  center: [number, number];
  longM: number;
  shortM: number;
  bearing: number;
} | null {
  const venue = venueFrame(model);
  if (!venue) return null;
  const bearingRad = (venue.bearing * Math.PI) / 180;
  const kx = Math.cos((venue.center[1] * Math.PI) / 180);
  const rotate = (lng: number, lat: number) => {
    const x = (lng - venue.center[0]) * 111320 * kx;
    const y = (lat - venue.center[1]) * 111320;
    return {
      along: x * Math.cos(bearingRad) - y * Math.sin(bearingRad),
      across: x * Math.sin(bearingRad) + y * Math.cos(bearingRad),
    };
  };

  let minAlong = Infinity;
  let maxAlong = -Infinity;
  let minAcross = Infinity;
  let maxAcross = -Infinity;
  for (const f of model.features) {
    const coords: Array<[number, number]> =
      f.geometry.type === 'Polygon'
        ? (f.geometry.coordinates as [number, number][][]).flat()
        : [[f.anchorLng, f.anchorLat]];
    for (const [lng, lat] of coords) {
      const { along, across } = rotate(lng, lat);
      minAlong = Math.min(minAlong, along);
      maxAlong = Math.max(maxAlong, along);
      minAcross = Math.min(minAcross, across);
      maxAcross = Math.max(maxAcross, across);
    }
  }
  if (!Number.isFinite(minAlong)) return null;

  // Centre of that oriented box, back into lng/lat.
  const cAlong = (minAlong + maxAlong) / 2;
  const cAcross = (minAcross + maxAcross) / 2;
  const cx = cAlong * Math.cos(bearingRad) + cAcross * Math.sin(bearingRad);
  const cy = -cAlong * Math.sin(bearingRad) + cAcross * Math.cos(bearingRad);
  return {
    center: [venue.center[0] + cx / (111320 * kx), venue.center[1] + cy / 111320],
    longM: maxAlong - minAlong,
    shortM: maxAcross - minAcross,
    bearing: venue.bearing,
  };
}

// --- zones (design passoff §2) ----------------------------------------------

/** One hue, three values: access reads from the edge, type from the name. */
export type ZoneFamily = 'restricted' | 'open' | 'outside';

export function zoneFamily(kind: FeatureKind): ZoneFamily {
  // Only VIP keeps the credentials dash. ADA check-in is an ops zone like
  // the box office (the operator flagged the two rendering differently), and
  // ADA viewing platforms are points anyway. The lots — parking and the
  // bike lot — share the slate outside-the-fence family.
  if (kind === 'vip') return 'restricted';
  if (kind === 'camping' || kind === 'bike') return 'outside';
  return 'open';
}

/**
 * Corner radius for AUTHORED shapes (zones, the fence), in metres. The ground
 * classes each carry their own radius in terrain.py's RADIUS_M_BY_CLASS; this
 * is the same table's default for anything it does not name — the handoff's
 * "rx 12-14 on anything rectangular". One value for both the fence and every
 * zone fill, so the fence and the venue fill trace one curve.
 */
export const AUTHORED_CORNER_RADIUS_M = 12;

/**
 * Constant-radius corner fillet — the same operation terrain.py's
 * round_corners() applies to the ground (prii-ground-corners-and-texture.md
 * §1), so an authored rectangle and the lot surface under it round alike.
 *
 * Every earlier attempt cut corners by a FRACTION of the adjacent edges
 * (Chaikin, then a 12% cut), which has no notion of a radius: a long edge
 * bowed over its whole length and a rectangle became a lozenge or a hexagon.
 * This trims a FIXED distance back from each vertex and joins the two trim
 * points with a short arc, so straight stays straight and only the corner
 * rounds. The radius is clamped per corner to 40% of the shorter adjacent
 * edge, so a short edge is never consumed by the corners at either end.
 */
export function roundCorners(
  ring: Array<[number, number]>,
  radiusM = AUTHORED_CORNER_RADIUS_M,
  arcPts = 4,
): Array<[number, number]> {
  if (ring.length < 4) return ring;
  const closed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
  const src = closed ? ring.slice(0, -1) : ring;
  const kx = 111320 * Math.cos((ring[0][1] * Math.PI) / 180);
  const ky = 111320;
  const pts = src.map(([lng, lat]) => [lng * kx, lat * ky]);
  const n = pts.length;
  const out: Array<[number, number]> = [];
  for (let i = 0; i < n; i += 1) {
    const prev = pts[(i - 1 + n) % n];
    const cur = pts[i];
    const nxt = pts[(i + 1) % n];
    const v1 = [prev[0] - cur[0], prev[1] - cur[1]];
    const v2 = [nxt[0] - cur[0], nxt[1] - cur[1]];
    const l1 = Math.hypot(v1[0], v1[1]);
    const l2 = Math.hypot(v2[0], v2[1]);
    if (l1 < 1e-6 || l2 < 1e-6) {
      out.push([cur[0], cur[1]]);
      continue;
    }
    // A corner already straighter than ~170° is left alone: rounding it adds
    // points and changes nothing a person can see.
    const cosang = (v1[0] * v2[0] + v1[1] * v2[1]) / (l1 * l2);
    if (cosang < -0.985) {
      out.push([cur[0], cur[1]]);
      continue;
    }
    const r = Math.min(radiusM, 0.4 * l1, 0.4 * l2);
    const a = [cur[0] + (v1[0] / l1) * r, cur[1] + (v1[1] / l1) * r];
    const b = [cur[0] + (v2[0] / l2) * r, cur[1] + (v2[1] / l2) * r];
    // Quadratic through the corner reads as a circular arc at map scale.
    out.push([a[0], a[1]]);
    for (let k = 1; k < arcPts; k += 1) {
      const t = k / arcPts;
      const mt = 1 - t;
      out.push([
        mt * mt * a[0] + 2 * mt * t * cur[0] + t * t * b[0],
        mt * mt * a[1] + 2 * mt * t * cur[1] + t * t * b[1],
      ]);
    }
    out.push([b[0], b[1]]);
  }
  const res: Array<[number, number]> = out.map(([x, y]) => [x / kx, y / ky]);
  if (closed) res.push([res[0][0], res[0][1]]);
  return res;
}

/** Zone polygons as one FeatureCollection with a `family` property, corners
 * filleted at AUTHORED_CORNER_RADIUS_M. ga_area is included — as the
 * open-family base wash that lifts the venue off the city — but its label is
 * suppressed elsewhere. */
export function zoneCollection(model: FestivalMapModel): object {
  const features = [];
  for (const f of model.features) {
    // ANY polygon feature is a zone — authoring a polygon IS the statement
    // that this thing has an official area. Every zone draws ALWAYS, lots
    // included (operator: "persistent the same way VIP is") — chips gate
    // only the icon tiles inside them.
    if (f.geometry.type !== 'Polygon') continue;
    // The same fillet, at the same radius, as fenceRing() — the ga_area fill
    // and the fence drawn over it must trace one curve, or the corners of
    // the venue show as dark cut-offs between the two.
    const rings = (f.geometry.coordinates as [number, number][][]).map((ring) =>
      roundCorners(ring),
    );
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: rings },
      properties: {
        family: f.kind === 'parking' ? 'outside' : zoneFamily(f.kind),
        kind: f.kind,
        fid: f.id,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** Stall spacing along an authored lot, in metres. The design's Lot 4 South
 * board draws about eleven stalls across the lot's ~290 m, so a stall is a
 * band of spaces, not one space (a real bay is under 3 m). */
export const LOT_STALL_SPACING_M = 24;
/** How far the stalls stay in from the lot's edge, so no line pokes past the
 * 12 m corner fillet the zone fill draws with. */
const LOT_STALL_INSET_M = 10;

/**
 * Stall lines for every authored lot (parking, bike), as real geometry: lines
 * across the lot's SHORT axis, spaced along its long axis. Geometry rather
 * than a fill-pattern because MapLibre anchors patterns to the tile grid — a
 * tile's vertical rules are vertical only at north-up, and the map opens on
 * the venue axis, where they showed as diagonals. Lines on the ground follow
 * the lot at any bearing and scale with it like painted stalls do.
 */
export function lotStallCollection(model: FestivalMapModel): object {
  const features = [];
  for (const f of model.features) {
    if (f.geometry.type !== 'Polygon') continue;
    if (f.kind !== 'parking' && f.kind !== 'bike') continue;
    const ring = (f.geometry.coordinates as [number, number][][])[0] ?? [];
    if (ring.length < 4) continue;
    const lat0 = ring.reduce((sum, p) => sum + p[1], 0) / ring.length;
    const lng0 = ring.reduce((sum, p) => sum + p[0], 0) / ring.length;
    const kx = Math.cos((lat0 * Math.PI) / 180);
    const m = ring.map((p): [number, number] => [
      (p[0] - lng0) * 111320 * kx,
      (p[1] - lat0) * 111320,
    ]);
    // The lot's long axis is its longest edge (orientedRect's rule).
    let bestLen = -1;
    let angle = 0;
    for (let i = 0; i < m.length - 1; i += 1) {
      const dx = m[i + 1][0] - m[i][0];
      const dy = m[i + 1][1] - m[i][1];
      const len = Math.hypot(dx, dy);
      if (len > bestLen) {
        bestLen = len;
        angle = Math.atan2(dy, dx);
      }
    }
    const c = Math.cos(-angle);
    const sn = Math.sin(-angle);
    const rot = m.map(([x, y]): [number, number] => [x * c - y * sn, x * sn + y * c]);
    const xs = rot.map((p) => p[0]);
    const ys = rot.map((p) => p[1]);
    const minX = Math.min(...xs) + LOT_STALL_INSET_M;
    const maxX = Math.max(...xs) - LOT_STALL_INSET_M;
    const minY = Math.min(...ys) + LOT_STALL_INSET_M;
    const maxY = Math.max(...ys) - LOT_STALL_INSET_M;
    if (maxX <= minX || maxY <= minY) continue;
    const back = ([x, y]: [number, number]): [number, number] => {
      const bx = x * Math.cos(angle) - y * Math.sin(angle);
      const by = x * Math.sin(angle) + y * Math.cos(angle);
      return [lng0 + bx / (111320 * kx), lat0 + by / 111320];
    };
    // Centred on the lot so the two end gaps match.
    const count = Math.floor((maxX - minX) / LOT_STALL_SPACING_M);
    const start = minX + (maxX - minX - count * LOT_STALL_SPACING_M) / 2;
    for (let k = 0; k <= count; k += 1) {
      const x = start + k * LOT_STALL_SPACING_M;
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [back([x, minY]), back([x, maxY])] },
        properties: { fid: f.id },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

// --- gates as breaks in the fence (design passoff §4) ------------------------

export type GateCut = {
  id: string;
  /** Ids of the entrance/exit features this cut was built from.
   *
   * `id` is the cut's own synthetic handle; these are the real features. A gate
   * is reachable two ways — tapping the arrow, or tapping its chip — and the
   * chip only knows about features, so without these the chip could select a
   * gate and the arrow would not light. A paired gate carries both ids, so
   * either one lights the shared arrow. */
  featureIds: string[];
  name: string | null;
  direction: 'in' | 'out' | 'both';
  /** Metres along the fence ring from its first vertex. */
  distM: number;
  point: [number, number];
  /** Compass bearing of the fence at the cut (direction of increasing dist). */
  tangentDeg: number;
  /** Compass bearing pointing INTO the venue at the cut. */
  inwardDeg: number;
};

function fenceRing(model: FestivalMapModel): Array<[number, number]> | null {
  const fence = model.features.find((f) => f.kind === 'ga_area' && f.geometry.type === 'Polygon');
  if (!fence) return null;
  const ring = (fence.geometry.coordinates as [number, number][][])[0];
  if (!ring || ring.length < 4) return null;
  // The authored ring with its corners filleted — the SAME operation at the
  // SAME radius zoneCollection() applies to the ga_area fill, so the fence
  // and the fill trace one curve. Earlier smoothing attempts (Chaikin, then
  // 12% corner cuts) had no radius: they turned the border into hexagons and
  // pushed the Beach Club outside its own venue. A 12 m fillet on a venue
  // hundreds of metres across leaves every edge straight and only the
  // corners soft.
  return roundCorners(ring);
}

/**
 * Project each paired gate onto the fence ring. A gate is not a pin — it is
 * the one place the fence stops — so everything about its mark hangs off a
 * position ON the perimeter: distance along the ring, the fence's own
 * tangent there, and which way is inside.
 */
export function gateCuts(
  model: FestivalMapModel,
  gates: Array<{
    entrance: { id: string } | null;
    exit: { id: string } | null;
    lat: number;
    lng: number;
    name?: string | null;
  }>,
): GateCut[] {
  const ring = fenceRing(model);
  if (!ring) return [];

  // An entrance is two-way unless the edition says otherwise.
  //
  // Festival gates overwhelmingly run both ways, and a map that names only
  // entrances is describing where the openings ARE — it is not asserting you
  // cannot leave through them. Drawing those as in-only invents a restriction
  // the venue never stated, and leaves the map with no way out at all. Once an
  // edition authors even ONE exit it is making the distinction deliberately,
  // and unpaired entrances go back to in-only.
  const authorsExits = model.features.some((f) => f.kind === 'exit');

  const kx = Math.cos((ring[0][1] * Math.PI) / 180) * 111320;
  const ky = 111320;
  const toM = (p: [number, number]) => ({ x: p[0] * kx, y: p[1] * ky });

  // Cumulative distance per vertex.
  const cum = [0];
  for (let i = 1; i < ring.length; i += 1) {
    const a = toM(ring[i - 1]);
    const b = toM(ring[i]);
    cum.push(cum[i - 1] + Math.hypot(b.x - a.x, b.y - a.y));
  }

  // Venue centre for the inward test.
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    cx += ring[i][0];
    cy += ring[i][1];
  }
  cx /= ring.length - 1;
  cy /= ring.length - 1;
  const centre = toM([cx, cy]);

  const cuts: GateCut[] = [];
  for (const gate of gates) {
    const g = toM([gate.lng, gate.lat]);
    let best: { d2: number; dist: number; point: [number, number]; seg: number; t: number } | null =
      null;
    for (let i = 0; i < ring.length - 1; i += 1) {
      const a = toM(ring[i]);
      const b = toM(ring[i + 1]);
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const len2 = abx * abx + aby * aby || 1e-12;
      const t = Math.max(0, Math.min(1, ((g.x - a.x) * abx + (g.y - a.y) * aby) / len2));
      const px = a.x + abx * t;
      const py = a.y + aby * t;
      const d2 = (g.x - px) ** 2 + (g.y - py) ** 2;
      if (!best || d2 < best.d2) {
        best = {
          d2,
          dist: cum[i] + Math.sqrt(len2) * t,
          point: [px / kx, py / ky],
          seg: i,
          t,
        };
      }
    }
    if (!best) continue;
    const a = toM(ring[best.seg]);
    const b = toM(ring[best.seg + 1]);
    const tangentDeg = ((Math.atan2(b.x - a.x, b.y - a.y) * 180) / Math.PI + 360) % 360;
    // Inward = the perpendicular whose dot product with (centre - point) is
    // positive.
    const nx = -(b.y - a.y);
    const ny = b.x - a.x;
    const px = a.x + (b.x - a.x) * best.t;
    const py = a.y + (b.y - a.y) * best.t;
    const inwardSign = nx * (centre.x - px) + ny * (centre.y - py) >= 0 ? 1 : -1;
    const inwardDeg = ((Math.atan2(nx * inwardSign, ny * inwardSign) * 180) / Math.PI + 360) % 360;
    cuts.push({
      id: `gate-${cuts.length}`,
      featureIds: [gate.entrance?.id, gate.exit?.id].filter(
        (id): id is string => typeof id === 'string',
      ),
      name: gate.name ?? null,
      direction:
        gate.entrance && gate.exit
          ? 'both'
          : gate.entrance
            ? authorsExits
              ? 'in'
              : 'both'
            : 'out',
      distM: best.dist,
      point: best.point,
      tangentDeg,
      inwardDeg,
    });
  }
  return cuts.sort((a, b) => a.distM - b.distM);
}

/** A point `distM` along the fence ring. */
function ringPointAt(
  ring: Array<[number, number]>,
  cum: number[],
  distM: number,
): [number, number] {
  const total = cum[cum.length - 1];
  const d = ((distM % total) + total) % total;
  for (let i = 1; i < cum.length; i += 1) {
    if (cum[i] >= d) {
      const span = cum[i] - cum[i - 1] || 1e-12;
      const t = (d - cum[i - 1]) / span;
      return [
        ring[i - 1][0] + (ring[i][0] - ring[i - 1][0]) * t,
        ring[i - 1][1] + (ring[i][1] - ring[i - 1][1]) * t,
      ];
    }
  }
  return ring[0];
}

/**
 * The fence with its gate gaps cut out, plus the brightened runs either side
 * of each gap (the 0.35 hairline alone is too faint over terrain for a gap to
 * read as an interruption).
 */
export function fenceWithGateGaps(
  model: FestivalMapModel,
  cuts: GateCut[],
  { gapM = 14, brightM = 16 } = {},
): { fence: object; bright: object; gapEnds: Array<[[number, number], [number, number]]> } {
  const ring = fenceRing(model);
  const empty = { type: 'FeatureCollection', features: [] };
  if (!ring) return { fence: empty, bright: empty, gapEnds: [] };
  if (!cuts.length) {
    return {
      fence: {
        type: 'FeatureCollection',
        features: [
          { type: 'Feature', geometry: { type: 'LineString', coordinates: ring }, properties: {} },
        ],
      },
      bright: empty,
      gapEnds: [],
    };
  }
  const kx = Math.cos((ring[0][1] * Math.PI) / 180) * 111320;
  const cum = [0];
  for (let i = 1; i < ring.length; i += 1) {
    cum.push(
      cum[i - 1] +
        Math.hypot((ring[i][0] - ring[i - 1][0]) * kx, (ring[i][1] - ring[i - 1][1]) * 111320),
    );
  }
  const total = cum[cum.length - 1];

  // Vertices must be emitted in ROTATED order (by distance from the run's
  // start), not in stored ring order — a run that wraps past the ring's
  // first vertex otherwise stitches points out of sequence and draws a chord
  // straight across the venue (the operator's "border grabbing from the top
  // left corner" screenshot).
  const lineBetween = (fromM: number, toM_: number): Array<[number, number]> => {
    const start = ((fromM % total) + total) % total;
    let span = toM_ - fromM;
    if (span <= 0) span += total;
    const inRun: Array<[number, [number, number]]> = [];
    for (let i = 0; i < cum.length - 1; i += 1) {
      const rel = (((cum[i] - start) % total) + total) % total;
      if (rel > 1e-6 && rel < span - 1e-6) inRun.push([rel, ring[i]]);
    }
    inRun.sort((a, b) => a[0] - b[0]);
    return [ringPointAt(ring, cum, fromM), ...inRun.map((v) => v[1]), ringPointAt(ring, cum, toM_)];
  };

  const fenceFeatures = [];
  const brightFeatures = [];
  const gapEnds: Array<[[number, number], [number, number]]> = [];
  for (let i = 0; i < cuts.length; i += 1) {
    const cut = cuts[i];
    const next = cuts[(i + 1) % cuts.length];
    // A two-way opening is wider than a one-way (passoff: 30 vs 20 at close).
    const gap = cut.direction === 'both' ? gapM * 1.5 : gapM;
    const gapStart = cut.distM - gap / 2;
    const gapEnd = cut.distM + gap / 2;
    const nextGapStart = next.distM - gapM / 2 + (i === cuts.length - 1 ? total : 0);
    gapEnds.push([ringPointAt(ring, cum, gapStart), ringPointAt(ring, cum, gapEnd)]);
    fenceFeatures.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: lineBetween(gapEnd, nextGapStart) },
      properties: {},
    });
    brightFeatures.push(
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: lineBetween(gapStart - brightM, gapStart) },
        properties: {},
      },
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: lineBetween(gapEnd, gapEnd + brightM) },
        properties: {},
      },
    );
  }
  return {
    fence: { type: 'FeatureCollection', features: fenceFeatures },
    bright: { type: 'FeatureCollection', features: brightFeatures },
    gapEnds,
  };
}

/** Zoom that fits `meters` across `pixels` at this latitude (512px tiles). */
export function zoomForMeters(meters: number, pixels: number, lat: number): number {
  const worldM = 40075016.686 * Math.cos((lat * Math.PI) / 180);
  return Math.log2((worldM * pixels) / (512 * meters));
}

/**
 * The camera's pan clamp, in the venue's own along/across frame.
 *
 * Axis-aligned lat/lng limits are wrong on a rotated map: their edges meet the
 * screen as diagonals, so hitting one mid-pan feels like sliding along a fence
 * built askew. Half-extents about the site centre, measured along the venue's
 * own axes, give straight screen-edge stops instead.
 */
export type CameraLimits = {
  center: [number, number];
  bearing: number;
  maxAlong: number;
  maxAcross: number;
};

/**
 * Grow the clamp until a point is inside it, and report the span the camera
 * must be able to show to hold the site and that point together.
 *
 * This is what lets you find YOURSELF on the map from anywhere. The clamp is
 * otherwise sized to the site + a few hundred metres, which is correct until
 * a user opens the map from a mile away: their puck draws, and no pan or zoom
 * could ever bring it on screen.
 *
 * `spanM` is a diameter about the site centre, not the distance between the
 * two — the camera is still clamped near the site, so the panel has to reach
 * out and back. It uses the true distance rather than the larger axis: a point
 * offset diagonally is further away than either of its components, and sizing
 * the panel to the component would leave it just off the corner.
 */
export function limitsReaching(
  base: CameraLimits,
  point: [number, number],
  extraM: number,
): { limits: CameraLimits; spanM: number } {
  const bearingRad = (base.bearing * Math.PI) / 180;
  const kx = Math.cos((base.center[1] * Math.PI) / 180) * 111320;
  const x = (point[0] - base.center[0]) * kx;
  const y = (point[1] - base.center[1]) * 111320;
  const along = Math.abs(x * Math.cos(bearingRad) - y * Math.sin(bearingRad));
  const across = Math.abs(x * Math.sin(bearingRad) + y * Math.cos(bearingRad));
  return {
    limits: {
      ...base,
      maxAlong: Math.max(base.maxAlong, along + extraM),
      maxAcross: Math.max(base.maxAcross, across + extraM),
    },
    spanM: 2 * Math.hypot(along, across),
  };
}

/** Where a zone's label sits: its centroid, closing vertex dropped. */
export function polygonLabelPoint(feature: MapFeature): [number, number] | null {
  if (feature.geometry.type !== 'Polygon') return null;
  const ring = (feature.geometry.coordinates as [number, number][][])[0] ?? [];
  if (!ring.length) return null;
  const last =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.length - 1
      : ring.length;
  let lng = 0;
  let lat = 0;
  for (let i = 0; i < last; i += 1) {
    lng += ring[i][0];
    lat += ring[i][1];
  }
  return [lng / last, lat / last];
}

/** Camera bounds: stored bounds, else derived from features, else null. */
export function cameraBounds(model: FestivalMapModel): [[number, number], [number, number]] | null {
  let { bounds } = model;
  if (!bounds) {
    if (!model.features.length) return null;
    const lngs = model.features.map((f) => f.anchorLng);
    const lats = model.features.map((f) => f.anchorLat);
    bounds = {
      west: Math.min(...lngs),
      east: Math.max(...lngs),
      south: Math.min(...lats),
      north: Math.max(...lats),
    };
  }
  return [
    [bounds.west, bounds.south],
    [bounds.east, bounds.north],
  ];
}
